// 소통방 activity — "posted since you last looked", for the tab bar's badge
// (Eddie 2026-09-16: a post had been up for two hours and the tab said
// nothing). Read-only: one live query over the newest threads, counted
// against a stamp this device keeps. Nothing is written to Firestore, so no
// rule and no schema moves for it.
//
// The stamp is per device (localStorage). A member who reads the room on
// their laptop still sees the badge on their phone — the honest trade for
// not writing a new field onto the user doc; a synced stamp is a follow-up.
import { db } from "@/firebaseConfig";
import { noteFailure } from "@/utils/silentFailure";
import { roomStore } from "@/services/roomStore";

const WATCH = 30; // the newest N posts — a badge never needs more
const KEY = (uid) => `yp.roomSeen.v1.${uid || "anon"}`;

// The uid and stamp the live count is running against. Held here, not in the
// watcher's closure, so marking the room read moves the NEXT snapshot's
// baseline too — otherwise the count reappears on the following post.
let watching = null;
let since = "";

/** The stamp this device last marked the room read at (ISO, the thread's own
 *  shape). First ever run stamps NOW, so a new device opens on 0 instead of
 *  announcing thirty old posts. */
export function seenAt(uid) {
  try {
    const got = localStorage.getItem(KEY(uid));
    if (got) return got;
    const now = new Date().toISOString();
    localStorage.setItem(KEY(uid), now);
    return now;
  } catch (e) {
    return new Date().toISOString();
  }
}

/** Posts newer than the stamp; mine and deleted ones never count. */
export function countFresh(threads, uid, stamp) {
  return (threads || []).filter((t) => !t.deleted
    && t.createdBy !== uid
    && String(t.createdAt || "") > stamp).length;
}

/** The room has been looked at: the count drops to zero and stays there until
 *  the next post. Called with no uid it uses the one being watched, which is
 *  what the room itself does — its own userInfo can still be empty on mount. */
export function markRoomSeen(uid) {
  const who = uid || watching;
  since = new Date().toISOString();
  try { localStorage.setItem(KEY(who), since); } catch (e) { /* the badge just stays up */ }
  roomStore.fresh = 0;
}

export default {
  /** live count → roomStore.fresh; returns the unsubscribe */
  watchFresh(uid) {
    if (!uid) return () => {};
    watching = uid;
    since = seenAt(uid);
    const off = db.collection("threads").orderBy("createdAt", "desc").limit(WATCH)
      .onSnapshot(
        (s) => { roomStore.fresh = countFresh(s.docs.map((d) => d.data()), watching, since); },
        (e) => noteFailure("roomActivity.watchFresh", e),
      );
    return () => { off(); watching = null; };
  },
};
